/**
 * El cuestionario.
 *
 * El estado vive en el servidor, no aquí. Esta pantalla es un lector de lo que
 * hay guardado y un emisor de parches: recargar, cambiar de dispositivo o
 * cerrar la pestaña a media pregunta no pierde nada, porque nunca hubo nada
 * importante solo en memoria.
 *
 * El paso va en la URL. Es lo que permite volver atrás con el botón del
 * navegador, compartir por dónde vas y que un enlace del correo aterrice en el
 * sitio correcto.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Logo } from "../components/Logo";
import { BarraSesion } from "../components/Sesion";
import { STEP_META } from "../components/wizard/steps";
import api, {
  ApiError,
  useAutosave,
  useSession,
  useWizardContent,
  type SaveState,
} from "../lib/sessions";

function Indicador({ estado }: { estado: SaveState }) {
  const texto: Record<SaveState, string> = {
    idle: "",
    saving: "Guardando…",
    saved: "Guardado",
    error: "No se ha podido guardar",
  };
  return (
    <span
      role="status"
      aria-live="polite"
      className={
        "text-sm tabular-nums " + (estado === "error" ? "text-terracotta" : "text-ink/50")
      }
    >
      {texto[estado]}
    </span>
  );
}

function Barra({ percent }: { percent: number }) {
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-paper-2">
      <div
        className="h-full rounded-full bg-ink transition-[width] duration-500"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

export default function Wizard() {
  const { sessionId, step } = useParams<{ sessionId: string; step: string }>();
  const navigate = useNavigate();

  const contenido = useWizardContent();
  const sesion = useSession(sessionId);
  const { save, flush, state } = useAutosave(sessionId, step ?? "");

  const [error, setError] = useState<string | null>(null);
  const [faltan, setFaltan] = useState<string[]>([]);
  const [generando, setGenerando] = useState(false);

  const pasos = contenido.data?.steps ?? [];
  const indice = step ? pasos.indexOf(step) : -1;
  const meta = step ? STEP_META[step] : undefined;

  const respuestas = useMemo(
    () => (step ? (sesion.data?.answers?.[step] ?? {}) : {}),
    [sesion.data, step],
  );

  // Un paso que no existe en el cuestionario manda al primero en vez de dejar
  // la pantalla en blanco: pasa al pegar una URL vieja tras cambiar los pasos.
  useEffect(() => {
    if (contenido.data && step && indice === -1) {
      navigate(`/cuestionario/${sessionId}/${pasos[0]}`, { replace: true });
    }
  }, [contenido.data, step, indice, navigate, sessionId, pasos]);

  if (contenido.isLoading || sesion.isLoading) {
    return <Pantalla>Cargando el cuestionario…</Pantalla>;
  }
  if (contenido.isError || sesion.isError) {
    return <Pantalla>No se ha podido cargar la sesión. Vuelve a intentarlo.</Pantalla>;
  }
  if (!contenido.data || !sesion.data || !meta || indice === -1) {
    return <Pantalla>Preparando…</Pantalla>;
  }

  const ir = (destino: string) => {
    flush();
    navigate(`/cuestionario/${sessionId}/${destino}`);
    window.scrollTo({ top: 0 });
  };

  const generar = async () => {
    flush();
    setError(null);
    setFaltan([]);
    setGenerando(true);
    try {
      // Se espera a que salga el último guardado antes de pedir el informe: sin
      // esta pausa, el servidor podría calcular sin la última respuesta.
      await new Promise((r) => setTimeout(r, 900));
      const informe = await api.generateReport(sessionId as string);
      navigate(`/informe/${informe.session_id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setFaltan(e.missing);
        setError("Faltan bloques por completar antes de poder calcular el diagnóstico.");
      } else {
        setError("No se ha podido generar el informe. Inténtalo de nuevo en un momento.");
      }
    } finally {
      setGenerando(false);
    }
  };

  const Paso = meta.Component;
  const ultimo = indice === pasos.length - 1;
  const progreso = sesion.data.progress;

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-10 border-b border-line bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-4">
          <Logo />
          <div className="flex items-center gap-5">
            <Indicador estado={state} />
            <BarraSesion />
          </div>
        </div>
        <div className="mx-auto max-w-3xl px-6 pb-3">
          <Barra percent={progreso.percent} />
          <div className="mt-2 flex justify-between text-xs text-ink/50">
            <span>
              Paso {indice + 1} de {pasos.length}
            </span>
            <span className="tabular-nums">{progreso.percent}% completado</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="font-display text-3xl text-ink">{meta.title}</h1>
        <p className="mt-1 text-ink/60">{meta.subtitle}</p>

        <div className="mt-8">
          <Paso content={contenido.data} value={respuestas} save={save} />
        </div>

        {error ? (
          <div className="mt-8 rounded-tarjeta border border-terracotta/30 bg-white p-4">
            <p className="text-ink">{error}</p>
            {faltan.length > 0 ? (
              <ul className="mt-3 space-y-1">
                {faltan.map((f) => (
                  <li key={f}>
                    <button
                      type="button"
                      onClick={() => ir(f)}
                      className="text-sm text-teal underline underline-offset-2"
                    >
                      Ir a {STEP_META[f]?.title ?? f}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <nav className="mt-10 flex items-center justify-between border-t border-line pt-6">
          <button
            type="button"
            onClick={() => ir(pasos[indice - 1] as string)}
            disabled={indice === 0}
            className="text-label font-medium uppercase text-stone transition-colors duration-300 hover:text-ink disabled:invisible"
          >
            ← Anterior
          </button>

          {ultimo ? (
            <button
              type="button"
              onClick={generar}
              disabled={generando}
              className="critero-boton disabled:opacity-60"
            >
              {generando ? "Calculando el diagnóstico…" : "Generar mi diagnóstico"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => ir(pasos[indice + 1] as string)}
              className="critero-boton"
            >
              Continuar →
            </button>
          )}
        </nav>

        <ol className="mt-10 flex flex-wrap gap-2">
          {pasos.map((p, i) => {
            const hecho = progreso.completed.includes(p);
            const actual = p === step;
            return (
              <li key={p}>
                <button
                  type="button"
                  onClick={() => ir(p)}
                  title={STEP_META[p]?.title ?? p}
                  aria-current={actual ? "step" : undefined}
                  className={
                    "h-8 w-8 rounded-none border text-xs tabular-nums transition-colors duration-300 " +
                    (actual
                      ? "border-ink bg-ink text-paper"
                      : hecho
                        ? "border-line bg-paper-2 text-teal hover:border-teal"
                        : "border-line bg-white text-stone-2 hover:border-teal hover:text-teal")
                  }
                >
                  {i + 1}
                </button>
              </li>
            );
          })}
        </ol>
      </main>
    </div>
  );
}

function Pantalla({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6">
      <p className="text-ink/60">{children}</p>
    </div>
  );
}

/**
 * Abre una sesión nueva y entra en el primer paso.
 *
 * Aquí aterriza quien viene de critero.es a hacer el diagnóstico. Si no tiene
 * sesión, `Protegido` lo manda antes a Keycloak, donde también puede crear la
 * cuenta: el registro no es un muro delante del producto, es lo que permite
 * guardar un cuestionario de cuarenta minutos y volver a él.
 *
 * Ya no hay pre-relleno desde el test corto: ese test desapareció con el cambio
 * de modelo. El diagnóstico completo es lo gratuito, así que no hay nada que
 * arrastrar de un paso anterior.
 */
export function NuevaSesion() {
  const navigate = useNavigate();
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelado = false;

    async function abrir() {
      const sesion = await api.createSession();
      if (!cancelado) {
        navigate(`/cuestionario/${sesion.id}/${sesion.progress.steps[0]}`, { replace: true });
      }
    }

    abrir().catch(() => !cancelado && setError(true));
    return () => {
      cancelado = true;
    };
  }, [navigate]);

  if (error) return <Pantalla>No se ha podido abrir el cuestionario.</Pantalla>;
  return <Pantalla>Abriendo tu cuestionario…</Pantalla>;
}
